import React, { useState } from 'react';
import { IconClose } from '../../icons';
import ModalDefault from '../../ModalDefault';
import { ModalContactHead, ModalcontactMain as BaseModalcontactMain } from '../../SearchProfessionalProposal/styles';
import { comum } from '../../../styles/Themes/light';
import { PendenciaKanban, STATUS_CONCLUIDAS } from './types';
import { Badge } from './styles';
import Moment from 'react-moment';
import { Clock, File, ExternalLink } from 'lucide-react';
import styled from 'styled-components';
import { 
    CloseButtonHeader, 
    ModalSection, 
    Label, 
    Value, 
    FluxoBadge, 
    Button
} from './modalStyles';
import { formatTipo, isTipoFinanceiro, isTipoDocumentacao, isTipoInformativa, formatCurrency, TIPOS_PENDENCIA_LABELS_DESCRICAO } from './utils';
import api from '../../../services/api';
import { formatFileSize } from '../../../utility/byteSizeUtils';

const FileXCorner = ({ size = 24, color = "currentColor" }: { size?: number; color?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 22H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.706.706l3.588 3.588A2.4 2.4 0 0 1 20 8v5"/>
        <path d="M14 2v5a1 1 0 0 0 1 1h5"/>
        <path d="m15 17 5 5"/>
        <path d="m20 17-5 5"/>
    </svg>
);

const ModalcontactMain = styled(BaseModalcontactMain)`
    padding-bottom: 10px;
    overflow: visible !important;
    max-height: none !important;
    overflow-x: hidden !important;
    overflow-y: visible !important;
`;

interface ModalVisualizarPendenciaProps {
    modalAtivo: boolean;
    setModalAtivo: (ativo: boolean) => void;
    pendencia: PendenciaKanban | null;
    onNavigateToFile?: (fileId: number) => void;
}

const ArquivoItem = styled.div<{ $isExcluido?: boolean }>`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    border: 1px solid ${({ theme }) => theme.colors.interfacePure100};
    border-radius: 6px;
    background: ${({ theme, $isExcluido }) => $isExcluido ? 'transparent' : theme.colors.interfacePure50};
    transition: all 0.2s;
    
    &:hover {
        background: ${({ theme, $isExcluido }) => $isExcluido ? 'transparent' : theme.colors.interfacePure100};
    }
`;

const ArquivoInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
`;

const ArquivoNome = styled.span`
    font-size: 14px;
    color: ${({ theme }) => theme.colors.interfaceText400};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ArquivoTamanho = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.colors.interfaceText300};
    margin-left: 8px;
`;

const ArquivoInfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
`;

const NavigateButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.2s;
    color: ${({ theme }) => theme.colors.interfaceText300};
    
    &:hover {
        background: ${({ theme }) => theme.colors.interfacePure100};
        color: ${({ theme }) => theme.colors.interfaceText400};
    }
`;

const ArquivosContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow: visible;
    max-height: none;
`;

const ModalVisualizarPendencia: React.FC<ModalVisualizarPendenciaProps> = ({
    modalAtivo,
    setModalAtivo,
    pendencia,
    onNavigateToFile
}) => {
    if (!pendencia) return null;

    const handleArquivoClick = (arquivo: { id_proposta_arquivo: number; caminho: string }) => {
        if (onNavigateToFile && arquivo.id_proposta_arquivo) {
            onNavigateToFile(arquivo.id_proposta_arquivo);
            setModalAtivo(false);
        }
    };

    const getFileName = (caminho: string): string => {
        const parts = caminho.split('/').filter(Boolean);
        return parts[parts.length - 1] || caminho;
    };

    const getDestino = (): string => {
        if (!pendencia.destino) {
            return 'Operador';
        }
        
        const destinoMap: Record<string, string> = {
            'operador': 'Operador',
            'profissional': 'Profissional',
            'solicitante': 'Solicitante'
        };
        
        return destinoMap[pendencia.destino] || 'Operador';
    };

    const getTipoCriador = (): string => {
        if (pendencia.origem) {
            return pendencia.origem === 'Correspondente' ? 'Profissional' : pendencia.origem;
        }
        return 'Operador';
    };

    const getStatusLabel = (): string => {
        const status = pendencia.status_kanban;
        if (status === 'Pendentes') return 'Pendente';
        if (status === 'Concluídas') return 'Concluída';
        if (status === 'Em Andamento') return 'Em Andamento';
        return status;
    };

    const destino = getDestino();
    const tipoCriador = getTipoCriador();
    const statusLabel = getStatusLabel();
    const isFinanceira = isTipoFinanceiro(pendencia.tipo);
    const isDocumentacao = isTipoDocumentacao(pendencia.tipo);
    const isInformativa = isTipoInformativa(pendencia.tipo);
    const isConcluida = pendencia.status_kanban === STATUS_CONCLUIDAS;
    
    const labelDescricao = isFinanceira 
        ? (TIPOS_PENDENCIA_LABELS_DESCRICAO[pendencia.tipo] || 'Descrição')
        : 'Descrição';
    
    const getLabelResposta = (): string => {
        if (isFinanceira && pendencia.resultado === 'Recusada') {
            return 'Justificativa';
        }
        if (isDocumentacao) {
            return 'Informações sobre a documentação';
        }
        if (pendencia.tipo === 'orientacao') {
            return 'Orientação solicitada';
        }
        if (isInformativa) {
            return 'Informação solicitada';
        }
        return 'Resposta';
    };

    return (
        <ModalDefault
            id={'overlayModalPendencia'}
            onClose={() => setModalAtivo(false)}
            openModal={modalAtivo}
            width="fit-content"
            maxWidth="600px"
            maxHeight="90vh"
        >
            <ModalContactHead>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start', flex: 1, width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <div>
                            Detalhes da Pendência
                        </div>
                        <CloseButtonHeader onClick={() => setModalAtivo(false)}>
                            <IconClose color={comum.colors.primaryRed400} />
                        </CloseButtonHeader>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            {pendencia.status_kanban === STATUS_CONCLUIDAS && pendencia.resultado && (
                                <Badge 
                                    variant={
                                        pendencia.resultado === 'Resolvida' ? 'resolvida' :
                                        pendencia.resultado === 'Aceita' ? 'aceita' : 'recusada'
                                    }
                                >
                                    {pendencia.resultado}
                                </Badge>
                            )}
                        </div>
                        <span style={{ 
                            fontSize: '11px', 
                            color: comum.colors.interfaceText300,
                            fontFamily: 'monospace',
                            marginRight: '8px'
                        }}>
                            #{pendencia.id_pendencia}
                        </span>
                    </div>
                </div>
            </ModalContactHead>
            
            <ModalcontactMain>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {/* Tipo */}
                    <ModalSection>
                        <Label>Tipo</Label>
                        <Badge variant="tipo">
                            {formatTipo(pendencia.tipo)}
                        </Badge>
                    </ModalSection>

                    {/* Status */}
                    <ModalSection>
                        <Label>Status</Label>
                        <Value bold>{statusLabel}</Value>
                    </ModalSection>

                    {/* Criado por */}
                    <ModalSection>
                        <Label>Criado por</Label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            {pendencia.criadorNome ? (
                                <>
                                    <Value>{pendencia.criadorNome}</Value>
                                    <FluxoBadge variant="tipo">
                                        {tipoCriador}
                                    </FluxoBadge>
                                </>
                            ) : (
                                <Value style={{ color: comum.colors.interfaceText300 }}>
                                    {tipoCriador}
                                </Value>
                            )}
                        </div>
                    </ModalSection>

                    {/* Destinatário */}
                    <ModalSection>
                        <Label>Destinatário</Label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            {pendencia.destinatarioNome ? (
                                <>
                                    <Value>{pendencia.destinatarioNome}</Value>
                                    <FluxoBadge variant="tipo">
                                        {destino}
                                    </FluxoBadge>
                                </>
                            ) : (
                                <FluxoBadge variant="tipo">
                                    {destino}
                                </FluxoBadge>
                            )}
                        </div>
                    </ModalSection>

                    {/* Valor - apenas para pendências financeiras */}
                    {isFinanceira && pendencia.valor && (
                        <ModalSection>
                            <Label>Valor Solicitado</Label>
                            <Value style={{ fontWeight: 600, fontSize: '16px' }}>
                                {formatCurrency(pendencia.valor)}
                            </Value>
                        </ModalSection>
                    )}

                    {/* Descrição */}
                    <ModalSection>
                        <Label>{labelDescricao}</Label>
                        <Value>{pendencia.descricao}</Value>
                    </ModalSection>

                    {/* Resposta/Justificativa - apenas para pendências concluídas */}
                    {isConcluida && pendencia.resposta && (
                        <ModalSection>
                            <Label>{getLabelResposta()}</Label>
                            <Value>{pendencia.resposta}</Value>
                        </ModalSection>
                    )}

                    {/* Arquivos anexados - apenas para pendências concluídas com arquivos */}
                    {isConcluida && pendencia.arquivos && pendencia.arquivos.length > 0 && (
                        <ModalSection>
                            <Label>Arquivos anexados</Label>
                            <ArquivosContainer>
                                {pendencia.arquivos.map((arquivo) => {
                                    const isExcluido = (arquivo as any).excluido === true;
                                    return (
                                        <ArquivoItem 
                                            key={arquivo.id_proposta_arquivo}
                                            $isExcluido={isExcluido}
                                            style={{
                                                border: isExcluido ? '1px solid rgba(239, 68, 68, 0.3)' : undefined,
                                                opacity: isExcluido ? 0.75 : 1
                                            }}
                                        >
                                            <ArquivoInfo>
                                                {isExcluido ? (
                                                    <FileXCorner size={16} color="#EF4444" />
                                                ) : (
                                                    <File size={16} color={comum.colors.interfaceText300} />
                                                )}
                                                <ArquivoInfoContainer>
                                                    <ArquivoNome style={{ fontStyle: isExcluido ? 'italic' : 'normal' }}>
                                                        {getFileName(arquivo.caminho)}
                                                    </ArquivoNome>
                                                    {arquivo.tamanho && (
                                                        <ArquivoTamanho>
                                                            {formatFileSize(arquivo.tamanho)}
                                                        </ArquivoTamanho>
                                                    )}
                                                </ArquivoInfoContainer>
                                            </ArquivoInfo>
                                            {isExcluido ? (
                                                <div style={{
                                                    padding: '2px 8px',
                                                    borderRadius: '12px',
                                                    border: '1px solid rgba(239, 68, 68, 0.3)',
                                                    fontSize: '10px',
                                                    fontWeight: 500,
                                                    color: 'var(--cef4444)',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    Removido
                                                </div>
                                            ) : (
                                                onNavigateToFile && (
                                                    <NavigateButton
                                                        onClick={() => handleArquivoClick(arquivo)}
                                                    >
                                                        <ExternalLink size={16} />
                                                    </NavigateButton>
                                                )
                                            )}
                                        </ArquivoItem>
                                    );
                                })}
                            </ArquivosContainer>
                        </ModalSection>
                    )}

                    {/* Criado em */}
                    <ModalSection>
                        <Label>Criado em</Label>
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '6px',
                            fontSize: '14px',
                            color: comum.colors.interfaceText400
                        }}>
                            <Clock size={12} color={comum.colors.interfaceText300} />
                            <Moment locale="pt-br" format="DD/MM/YYYY [às] HH:mm">
                                {pendencia.criado}
                            </Moment>
                        </div>
                    </ModalSection>
                </div>

                <Button
                    type="button"
                    variant="primary"
                    onClick={() => setModalAtivo(false)}
                    style={{ width: '100%', marginTop: 16 }}
                >
                    Fechar
                </Button>
            </ModalcontactMain>
        </ModalDefault>
    );
};

export default ModalVisualizarPendencia;

